import { createFileRoute } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "../lib/store";
import { getThemeColors } from "../lib/theme";
import { useDuelsStore } from "../lib/games/duels/store";
import { useDuelsSocket } from "../lib/games/duels/useDuelsSocket";
import { Wikipage } from "../components/Wikipage";
import { ThemeToggle } from "../components/ThemeToggle";
import { HomeLink } from "../components/HomeLink";
import { ArticleCard } from "../components/ArticleCard";
import { Button } from "../components/ui/Button";
import { Timer } from "../components/Timer";
import { GameLoadingScreen } from "../components/GameLoadingScreen";
import { SectionHeader } from "../components/SectionHeader";
import { TargetBadge } from "../components/TargetBadge";
import { ArticleDialog } from "../components/ArticleDialog";
import { FullscreenButton } from "../components/FullscreenButton";
import { SvgIcon } from "../components/ui/SvgIcon";
import boltIcon from "../assets/bolt-icon.svg?raw";
import checkIcon from "../assets/check-icon.svg?raw";
import checkCircleIcon from "../assets/check-circle-icon.svg?raw";
import { LanguageSelector } from "../components/LanguageSelector";
import { MobileNav } from "../components/navbar/NavbarMobile";
import { NavButtons } from "../components/navbar/NavbarButtons";
import {
  appendArticleToPath,
  createOptimisticMove,
  createOptimisticReset,
  getDisplayedBestTry,
  getImprovedBestTry,
  getUsableOptimisticSelfState,
  shouldKeepOptimisticBestTry,
  type OptimisticSelfState,
} from "../lib/games/duels/optimistic";
import type {
  DuelPlayer,
  DuelPlayerInfo,
  DuelWinner,
} from "../lib/games/duels/types";
import type { Article } from "../lib/games/wikidaily/types";
import { useFullscreen } from "../hooks/useFullscreen";

const EMPTY_ARTICLE_PATH: Article[] = [];

type ScopedOptimisticSelfState = {
  gameKey: string;
  state: OptimisticSelfState;
};

type ScopedOptimisticBestTry = {
  gameKey: string;
  path: Article[];
};

type TargetHitFeedback = {
  gameKey: string;
  pathLength: number;
  improvedBest: boolean;
};

type ArticleRenderRequest = {
  gameKey: string;
  articleId: number;
  key: number;
};

const TARGET_FOUND_FEEDBACK_MS = 3000;
const DUEL_COUNTDOWN_MS = 3 * 60 * 1000;

const WINNER_CONFETTI_PIECES = [
  {
    left: 4,
    delay: 0,
    duration: 1300,
    colorClass: "bg-white",
    shapeClass: "h-3 w-1.5",
  },
  {
    left: 10,
    delay: 120,
    duration: 1500,
    colorClass: "bg-zinc-300",
    shapeClass: "h-2 w-2",
  },
  {
    left: 16,
    delay: 40,
    duration: 1450,
    colorClass: "bg-zinc-500",
    shapeClass: "h-4 w-1",
  },
  {
    left: 23,
    delay: 180,
    duration: 1550,
    colorClass: "bg-white",
    shapeClass: "h-2.5 w-1.5",
  },
  {
    left: 29,
    delay: 70,
    duration: 1400,
    colorClass: "bg-neutral-400",
    shapeClass: "h-3 w-1.5",
  },
  {
    left: 36,
    delay: 220,
    duration: 1600,
    colorClass: "bg-zinc-200",
    shapeClass: "h-2 w-2",
  },
  {
    left: 43,
    delay: 20,
    duration: 1500,
    colorClass: "bg-white",
    shapeClass: "h-4 w-1",
  },
  {
    left: 50,
    delay: 160,
    duration: 1350,
    colorClass: "bg-zinc-400",
    shapeClass: "h-2.5 w-1.5",
  },
  {
    left: 57,
    delay: 90,
    duration: 1550,
    colorClass: "bg-white",
    shapeClass: "h-3 w-1.5",
  },
  {
    left: 64,
    delay: 260,
    duration: 1450,
    colorClass: "bg-zinc-300",
    shapeClass: "h-2 w-2",
  },
  {
    left: 71,
    delay: 50,
    duration: 1650,
    colorClass: "bg-neutral-500",
    shapeClass: "h-4 w-1",
  },
  {
    left: 78,
    delay: 190,
    duration: 1400,
    colorClass: "bg-white",
    shapeClass: "h-2.5 w-1.5",
  },
  {
    left: 84,
    delay: 100,
    duration: 1500,
    colorClass: "bg-zinc-200",
    shapeClass: "h-3 w-1.5",
  },
  {
    left: 91,
    delay: 240,
    duration: 1600,
    colorClass: "bg-zinc-400",
    shapeClass: "h-2 w-2",
  },
  {
    left: 96,
    delay: 30,
    duration: 1450,
    colorClass: "bg-white",
    shapeClass: "h-4 w-1",
  },
] as const;

const LIGHT_WINNER_CONFETTI_PIECES = [
  {
    left: 4,
    delay: 0,
    duration: 1300,
    colorClass: "bg-gray-900",
    shapeClass: "h-3 w-1.5",
  },
  {
    left: 10,
    delay: 120,
    duration: 1500,
    colorClass: "bg-gray-500",
    shapeClass: "h-2 w-2",
  },
  {
    left: 16,
    delay: 40,
    duration: 1450,
    colorClass: "bg-gray-300",
    shapeClass: "h-4 w-1",
  },
  {
    left: 23,
    delay: 180,
    duration: 1550,
    colorClass: "bg-gray-800",
    shapeClass: "h-2.5 w-1.5",
  },
  {
    left: 29,
    delay: 70,
    duration: 1400,
    colorClass: "bg-gray-400",
    shapeClass: "h-3 w-1.5",
  },
  {
    left: 36,
    delay: 220,
    duration: 1600,
    colorClass: "bg-gray-700",
    shapeClass: "h-2 w-2",
  },
  {
    left: 43,
    delay: 20,
    duration: 1500,
    colorClass: "bg-gray-900",
    shapeClass: "h-4 w-1",
  },
  {
    left: 50,
    delay: 160,
    duration: 1350,
    colorClass: "bg-gray-500",
    shapeClass: "h-2.5 w-1.5",
  },
  {
    left: 57,
    delay: 90,
    duration: 1550,
    colorClass: "bg-gray-800",
    shapeClass: "h-3 w-1.5",
  },
  {
    left: 64,
    delay: 260,
    duration: 1450,
    colorClass: "bg-gray-300",
    shapeClass: "h-2 w-2",
  },
  {
    left: 71,
    delay: 50,
    duration: 1650,
    colorClass: "bg-gray-600",
    shapeClass: "h-4 w-1",
  },
  {
    left: 78,
    delay: 190,
    duration: 1400,
    colorClass: "bg-gray-900",
    shapeClass: "h-2.5 w-1.5",
  },
  {
    left: 84,
    delay: 100,
    duration: 1500,
    colorClass: "bg-gray-700",
    shapeClass: "h-3 w-1.5",
  },
  {
    left: 91,
    delay: 240,
    duration: 1600,
    colorClass: "bg-gray-400",
    shapeClass: "h-2 w-2",
  },
  {
    left: 96,
    delay: 30,
    duration: 1450,
    colorClass: "bg-gray-800",
    shapeClass: "h-4 w-1",
  },
] as const;

export const Route = createFileRoute("/duels")({
  component: DuelsPage,
});

export function DuelsPage() {
  const lang = useAppStore((state) => state.lang);
  const isDark = useAppStore((state) => state.isDark);
  const toggleTheme = useAppStore((state) => state.toggleTheme);
  const theme = getThemeColors(isDark);

  if (lang !== "en") {
    return (
      <EnglishOnlyDuels
        isDark={isDark}
        theme={theme}
        onToggleTheme={toggleTheme}
      />
    );
  }

  return (
    <DuelsGamePage isDark={isDark} theme={theme} onToggleTheme={toggleTheme} />
  );
}

function DuelsGamePage({
  isDark,
  theme,
  onToggleTheme,
}: {
  isDark: boolean;
  theme: ReturnType<typeof getThemeColors>;
  onToggleTheme: () => void;
}) {
  const { t } = useTranslation();
  const gameState = useDuelsStore((state) => state.gameState);
  const connectionError = useDuelsStore(
    (state) => state.localState.connectionError,
  );
  const queueStatus = useDuelsStore((state) => state.localState.queueStatus);
  const playerInfos = useDuelsStore((state) => state.localState.playerInfos);
  const { connected, reconnect, send } = useDuelsSocket();
  const autoRequeuedAbortRef = useRef<string | null>(null);
  const previousPhaseRef = useRef<string | undefined>(undefined);

  const phase = gameState?.type;
  const data = gameState?.data;
  const selfPlayer =
    data?.playerA?.id === data?.selfPlayerId
      ? data?.playerA
      : data?.playerB?.id === data?.selfPlayerId
        ? data?.playerB
        : undefined;
  const opponentPlayer =
    selfPlayer === data?.playerA
      ? data?.playerB
      : selfPlayer === data?.playerB
        ? data?.playerA
        : undefined;
  const selfPlayerName = getPlayerDisplayName(
    selfPlayer,
    playerInfos,
    t("duels.you"),
  );
  const opponentPlayerName = getPlayerDisplayName(
    opponentPlayer,
    playerInfos,
    t("duels.opponent"),
  );
  const playerAName = getPlayerDisplayName(
    data?.playerA,
    playerInfos,
    t("duels.playerA"),
  );
  const playerBName = getPlayerDisplayName(
    data?.playerB,
    playerInfos,
    t("duels.playerB"),
  );
  const winnerName = getWinnerDisplayName(
    data?.winner,
    data?.playerA,
    data?.playerB,
    playerInfos,
  );
  const winnerPlayer = getDuelWinnerPlayer(
    data?.winner,
    data?.playerA,
    data?.playerB,
  );
  const isSelfWinner = Boolean(
    winnerPlayer && winnerPlayer.id === data?.selfPlayerId,
  );
  const startArticle = data?.start;
  const endTime = data?.endTime;
  const duelGameKey = `${data?.selfPlayerId ?? "none"}:${data?.start?.id ?? "none"}:${data?.target?.id ?? "none"}:${data?.endTime ?? "none"}`;
  const navbarButtonsIngame = [
    NavButtons.wiki,
    NavButtons.playerPaths,
  ] as const;
  const [mobileTabIngame, setMobileTabIngame] = useState<
    (typeof navbarButtonsIngame)[number]
  >(NavButtons.wiki);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<{
    title: string;
    description: string | null;
  } | null>(null);
  const [articleRenderRequest, setArticleRenderRequest] =
    useState<ArticleRenderRequest | null>(null);
  const [optimisticSelfState, setOptimisticSelfState] =
    useState<ScopedOptimisticSelfState | null>(null);
  const [optimisticBestTry, setOptimisticBestTry] =
    useState<ScopedOptimisticBestTry | null>(null);
  const [targetHitFeedback, setTargetHitFeedback] =
    useState<TargetHitFeedback | null>(null);
  const loadedArticleIdRef = useRef<number | null>(null);
  const articleRenderRequestKeyRef = useRef(0);
  const initialArticleLoadGameKeyRef = useRef<string | null>(null);
  const targetHitFeedbackTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const serverCurrentTry = selfPlayer?.currentTry ?? EMPTY_ARTICLE_PATH;
  const optimisticSelfStateForGame =
    optimisticSelfState?.gameKey === duelGameKey
      ? optimisticSelfState.state
      : null;
  const articleRenderRequestForGame =
    articleRenderRequest?.gameKey === duelGameKey ? articleRenderRequest : null;
  const optimisticBestTryForGame =
    optimisticBestTry?.gameKey === duelGameKey &&
    shouldKeepOptimisticBestTry(optimisticBestTry.path, selfPlayer?.bestTry)
      ? optimisticBestTry.path
      : null;
  const targetHitFeedbackForGame =
    phase === "in_game" && targetHitFeedback?.gameKey === duelGameKey
      ? targetHitFeedback
      : null;
  const usableOptimisticSelfState =
    phase === "in_game"
      ? getUsableOptimisticSelfState(
          optimisticSelfStateForGame,
          serverCurrentTry,
        )
      : null;
  const displayedCurrentTry =
    usableOptimisticSelfState?.currentTry ?? serverCurrentTry;
  const displayedBestTry = getDisplayedBestTry(
    selfPlayer?.bestTry,
    optimisticBestTryForGame,
  );
  const finishButtonLabel = displayedBestTry.length
    ? t("duels.imDone")
    : t("duels.giveUp");
  const currentPathArticle =
    usableOptimisticSelfState?.currentArticle ??
    displayedCurrentTry.at(-1) ??
    startArticle;
  const reconcileArticleId =
    phase === "in_game" && !optimisticSelfStateForGame
      ? currentPathArticle?.id
      : null;
  const requestedArticleId =
    articleRenderRequestForGame?.articleId ?? reconcileArticleId;
  const requestedArticleKey = articleRenderRequestForGame?.key ?? 0;

  const loadArticle = useCallback(
    (article: Article | undefined, force = false) => {
      if (!article) return;
      if (!force && loadedArticleIdRef.current === article.id) return;

      loadedArticleIdRef.current = article.id;
      send({ action: "load_article", payload: article });
    },
    [send],
  );

  const reconcileArticle = useCallback(
    (article: Article | undefined) => {
      if (!article) return;

      articleRenderRequestKeyRef.current += 1;
      setArticleRenderRequest({
        gameKey: duelGameKey,
        articleId: article.id,
        key: articleRenderRequestKeyRef.current,
      });
      loadArticle(article, true);
    },
    [duelGameKey, loadArticle],
  );

  const loadStartArticle = useCallback(() => {
    reconcileArticle(startArticle);
  }, [reconcileArticle, startArticle]);

  const clearTargetHitFeedbackTimeout = useCallback(() => {
    if (!targetHitFeedbackTimeoutRef.current) return;

    clearTimeout(targetHitFeedbackTimeoutRef.current);
    targetHitFeedbackTimeoutRef.current = null;
  }, []);

  const handleWikiAction = useCallback(
    (action: { action: string; payload?: unknown }) => {
      send(action);

      if (action.action !== "move") return;

      const payload = action.payload as
        { id?: number; article?: number } | undefined;
      const movedArticleId = payload?.id ?? payload?.article;
      const movedArticle = getArticleFromWikiActionPayload(action.payload);
      if (!movedArticle) return;

      if (movedArticleId === data?.target?.id) {
        const completedPath = appendArticleToPath(
          displayedCurrentTry,
          movedArticle,
        );
        const improvedBestTry = getImprovedBestTry(
          optimisticBestTryForGame ?? selfPlayer?.bestTry,
          completedPath,
        );

        if (improvedBestTry) {
          setOptimisticBestTry({ gameKey: duelGameKey, path: improvedBestTry });
        }

        clearTargetHitFeedbackTimeout();
        setTargetHitFeedback({
          gameKey: duelGameKey,
          pathLength: completedPath.length,
          improvedBest: Boolean(improvedBestTry),
        });
        targetHitFeedbackTimeoutRef.current = setTimeout(() => {
          targetHitFeedbackTimeoutRef.current = null;
          setTargetHitFeedback(null);
          setOptimisticSelfState({
            gameKey: duelGameKey,
            state: createOptimisticReset(startArticle),
          });
          loadStartArticle();
        }, TARGET_FOUND_FEEDBACK_MS);
        return;
      }

      setOptimisticSelfState((current) => ({
        gameKey: duelGameKey,
        state: createOptimisticMove(
          current?.gameKey === duelGameKey
            ? current.state.currentTry
            : displayedCurrentTry,
          movedArticle,
        ),
      }));
      loadArticle(movedArticle);
    },
    [
      clearTargetHitFeedbackTimeout,
      data?.target?.id,
      displayedCurrentTry,
      duelGameKey,
      loadArticle,
      loadStartArticle,
      optimisticBestTryForGame,
      selfPlayer?.bestTry,
      send,
      startArticle,
    ],
  );

  const handleResetTry = useCallback(() => {
    clearTargetHitFeedbackTimeout();
    setTargetHitFeedback(null);
    send({ action: "reset_try" });
    setOptimisticSelfState({
      gameKey: duelGameKey,
      state: createOptimisticReset(startArticle),
    });
    loadStartArticle();
  }, [
    clearTargetHitFeedbackTimeout,
    duelGameKey,
    loadStartArticle,
    send,
    startArticle,
  ]);

  useEffect(() => {
    if (phase !== "in_game" || optimisticSelfStateForGame) return;

    const force = initialArticleLoadGameKeyRef.current !== duelGameKey;
    initialArticleLoadGameKeyRef.current = duelGameKey;
    loadArticle(currentPathArticle, force);
  }, [
    currentPathArticle,
    duelGameKey,
    loadArticle,
    optimisticSelfStateForGame,
    phase,
  ]);

  useEffect(() => {
    if (phase === "in_game") return;

    clearTargetHitFeedbackTimeout();
  }, [clearTargetHitFeedbackTimeout, phase]);

  useEffect(() => {
    clearTargetHitFeedbackTimeout();
  }, [clearTargetHitFeedbackTimeout, duelGameKey]);

  useEffect(() => {
    return () => clearTargetHitFeedbackTimeout();
  }, [clearTargetHitFeedbackTimeout]);

  useEffect(() => {
    for (const player of [data?.playerA, data?.playerB]) {
      if (!player || playerInfos[String(player.id)]) continue;
      send({ action: "player_info", payload: { id: player.id } });
    }
  }, [data?.playerA, data?.playerB, playerInfos, send]);

  useEffect(() => {
    if (phase !== "aborted" || !selfPlayer?.accepted) return;

    const abortKey = `${data?.endTime ?? "unknown"}-${data?.selfPlayerId ?? "unknown"}`;
    if (autoRequeuedAbortRef.current === abortKey) return;

    autoRequeuedAbortRef.current = abortKey;
    reconnect();
  }, [
    data?.endTime,
    data?.selfPlayerId,
    phase,
    reconnect,
    selfPlayer?.accepted,
  ]);

  useEffect(() => {
    if (phase === "duel_found" && previousPhaseRef.current !== "duel_found") {
      notifyDuelFound(
        t("duels.duelFound"),
        t("duels.opponentReady", { name: opponentPlayerName }),
      );
    }

    previousPhaseRef.current = phase;
  }, [opponentPlayerName, phase, t]);

  const countdownStartTime =
    phase === "in_game" && endTime
      ? new Date(endTime - DUEL_COUNTDOWN_MS).toISOString()
      : null;
  const acceptStartTime = endTime
    ? new Date(endTime - 30 * 1000).toISOString()
    : null;

  if (!gameState) {
    const loadingMessage =
      queueStatus === "searching"
        ? t("duels.searching")
        : t("duels.connecting");

    return (
      <div className={`h-dvh ${theme.bg} ${theme.text}`}>
        <GameLoadingScreen message={loadingMessage} showGrid={false} />
        <div className="fixed inset-x-0 bottom-8 mx-auto max-w-md px-4 text-center">
          <h1 className="mb-2 text-xl font-semibold">
            {queueStatus === "searching"
              ? t("duels.searchingMatch")
              : t("duels.connecting")}
          </h1>
          <p className={`text-sm ${theme.textSecondary}`}>
            {t("duels.waitingMatchmaking")}
          </p>
        </div>
        {connectionError && (
          <div className={`fixed bottom-4 left-4 text-sm ${theme.errorText}`}>
            {connectionError}
          </div>
        )}
      </div>
    );
  }

  if (phase === "duel_found") {
    return (
      <div className={`min-h-dvh ${theme.bg} ${theme.text}`}>
        <TopBar
          connected={connected}
          isDark={isDark}
          onToggleTheme={onToggleTheme}
          onOpenHelp={() => setIsHelpOpen(true)}
        />
        <main className="max-w-3xl mx-auto px-4 py-10 space-y-6">
          <div>
            <h1 className="text-3xl font-semibold">{t("duels.title")}</h1>
            <p className={`mt-2 ${theme.textSecondary}`}>
              {opponentPlayer
                ? t("duels.opponentFound", { name: opponentPlayerName })
                : t("duels.matchFound")}
            </p>
            <p className={`mt-2 text-sm ${theme.textSecondary}`}>
              {t("duels.shortestPathWins")}
            </p>
          </div>

          <DuelAcceptArticleCards
            start={data?.start}
            target={data?.target}
            theme={theme}
            isDark={isDark}
            onArticleSelect={setSelectedArticle}
          />

          {acceptStartTime && data?.endTime && (
            <div
              className={`flex items-center justify-between rounded-xl border ${theme.border} p-4`}
            >
              <span className="text-sm font-medium">
                {t("duels.acceptCountdown")}
              </span>
              <Timer
                startTime={acceptStartTime}
                duration={Math.max(
                  0,
                  Math.floor(
                    (data.endTime - new Date(acceptStartTime).getTime()) / 1000,
                  ),
                )}
                isDark={isDark}
              />
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-2">
            <AcceptStatus
              label={selfPlayerName}
              accepted={selfPlayer?.accepted ?? false}
              theme={theme}
            />
            <AcceptStatus
              label={opponentPlayerName}
              accepted={opponentPlayer?.accepted ?? false}
              theme={theme}
            />
          </div>

          <Button
            variant="primary"
            isDark={isDark}
            disabled={selfPlayer?.accepted}
            onClick={() => send({ action: "change_accept", payload: true })}
          >
            {selfPlayer?.accepted
              ? t("duels.accepted")
              : t("duels.acceptMatch")}
          </Button>
        </main>
        <DuelsHelpDialog
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          theme={theme}
          isDark={isDark}
        />
        {selectedArticle && (
          <ArticleDialog
            isOpen={!!selectedArticle}
            onClose={() => setSelectedArticle(null)}
            title={selectedArticle.title}
            description={selectedArticle.description}
            isDark={isDark}
          />
        )}
      </div>
    );
  }

  if (phase === "end_screen" || phase === "aborted") {
    return (
      <div className={`min-h-dvh ${theme.bg} ${theme.text}`}>
        <TopBar
          connected={connected}
          isDark={isDark}
          onToggleTheme={onToggleTheme}
          onOpenHelp={() => setIsHelpOpen(true)}
        />
        <main className="max-w-5xl mx-auto px-4 py-6 md:py-10 space-y-6">
          <DuelResultHero
            phase={phase}
            winner={data?.winner}
            winnerName={winnerName}
            selfPlayerName={selfPlayerName}
            opponentPlayerName={opponentPlayerName}
            selfPlayer={selfPlayer}
            opponentPlayer={opponentPlayer}
            isSelfWinner={isSelfWinner}
            theme={theme}
            isDark={isDark}
            onFindNewDuel={reconnect}
          />
          <div className="grid gap-4 md:grid-cols-2">
            <PathPanel
              title={t("duels.playerBestPath", { name: playerAName })}
              path={data?.playerA?.bestTry ?? []}
              theme={theme}
              isDark={isDark}
            />
            <PathPanel
              title={t("duels.playerBestPath", { name: playerBName })}
              path={data?.playerB?.bestTry ?? []}
              theme={theme}
              isDark={isDark}
            />
          </div>
        </main>
        <DuelsHelpDialog
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          theme={theme}
          isDark={isDark}
        />
      </div>
    );
  }

  return (
    <div
      className={`h-dvh ${theme.bg} ${theme.text} flex flex-col overflow-hidden`}
    >
      <TopBar
        connected={connected}
        isDark={isDark}
        onToggleTheme={onToggleTheme}
        onOpenHelp={() => setIsHelpOpen(true)}
        center={
          <DuelTargetSummary
            target={data?.target}
            theme={theme}
            isDark={isDark}
          />
        }
      >
        {countdownStartTime && data?.endTime && (
          <Timer
            startTime={countdownStartTime}
            duration={Math.max(
              0,
              Math.floor(
                (data.endTime - new Date(countdownStartTime).getTime()) / 1000,
              ),
            )}
            isDark={isDark}
            urgent
          />
        )}
      </TopBar>
      {countdownStartTime && data?.endTime && (
        <DuelCountdownPressureBar
          startTime={countdownStartTime}
          isDark={isDark}
        />
      )}

      <MobileNav
        currentTab={mobileTabIngame}
        onTabChange={setMobileTabIngame}
        isDark={isDark}
        theme={theme}
        buttons={navbarButtonsIngame}
        className="flex-1"
      >
        <main className="flex h-full min-h-0 overflow-hidden">
          <section
            className={`min-h-0 flex-1 overflow-hidden justify-center ${mobileTabIngame === NavButtons.wiki ? "flex" : "hidden md:flex"}`}
          >
            {targetHitFeedbackForGame ? (
              <TargetFoundFeedback
                pathLength={targetHitFeedbackForGame.pathLength}
                improvedBest={targetHitFeedbackForGame.improvedBest}
                targetTitle={data?.target?.title}
                theme={theme}
                isDark={isDark}
              />
            ) : (
              <Wikipage
                isDark={isDark}
                send={handleWikiAction}
                requestedArticleId={requestedArticleId}
                articleRequestKey={requestedArticleKey}
              />
            )}
          </section>

          <aside
            className={`border-l ${theme.border} p-4 overflow-hidden md:w-80 md:shrink-0 ${mobileTabIngame === NavButtons.playerPaths ? "flex flex-1 flex-col gap-4" : "hidden md:flex md:flex-col md:gap-4"}`}
          >
            <OpponentStatsPanel
              opponent={opponentPlayer}
              opponentName={opponentPlayerName}
              theme={theme}
              isDark={isDark}
            />
            <PathPanel
              title={t("duels.yourCurrentPath")}
              path={displayedCurrentTry}
              theme={theme}
              isDark={isDark}
            />
            <PathPanel
              title={t("duels.yourBestPath")}
              path={displayedBestTry}
              theme={theme}
              isDark={isDark}
            />
            <div className="grid shrink-0 gap-2">
              <Button
                variant="secondary"
                isDark={isDark}
                onClick={handleResetTry}
              >
                {t("duels.resetTry")}
              </Button>
              <Button
                variant={selfPlayer?.wantsFinish ? "primary" : "secondary"}
                isDark={isDark}
                onClick={() =>
                  send({
                    action: "request_finish",
                    payload: !selfPlayer?.wantsFinish,
                  })
                }
              >
                {selfPlayer?.wantsFinish
                  ? t("common.cancel")
                  : finishButtonLabel}
              </Button>
            </div>
          </aside>
        </main>
      </MobileNav>
      <DuelsHelpDialog
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        theme={theme}
        isDark={isDark}
      />
    </div>
  );
}

function getArticleFromWikiActionPayload(
  payload: unknown,
): Article | undefined {
  if (typeof payload !== "object" || payload === null) return undefined;

  const id =
    (payload as { id?: unknown; article?: unknown }).id ??
    (payload as { article?: unknown }).article;
  if (typeof id !== "number") return undefined;

  const title =
    (payload as { name?: unknown; title?: unknown }).name ??
    (payload as { title?: unknown }).title;

  return {
    id,
    title: typeof title === "string" ? title : "",
    description: null,
  };
}

function DuelCountdownPressureBar({
  startTime,
  isDark,
}: {
  startTime: string;
  isDark: boolean;
}) {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const elapsedMs = Math.min(
      DUEL_COUNTDOWN_MS,
      Math.max(0, Date.now() - new Date(startTime).getTime()),
    );
    if (barRef.current) {
      barRef.current.style.animationDelay = `-${elapsedMs}ms`;
    }
  }, [startTime]);

  return (
    <div
      className={`h-0.5 shrink-0 ${isDark ? "bg-red-950/40" : "bg-red-100"}`}
      aria-hidden="true"
    >
      <div
        ref={barRef}
        className={`duel-countdown-pressure h-full ${isDark ? "bg-red-500" : "bg-red-600"}`}
        style={
          {
            animationDuration: `${DUEL_COUNTDOWN_MS}ms`,
          } satisfies CSSProperties
        }
      />
    </div>
  );
}

function EnglishOnlyDuels({
  isDark,
  theme,
  onToggleTheme,
}: {
  isDark: boolean;
  theme: ReturnType<typeof getThemeColors>;
  onToggleTheme: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className={`min-h-dvh ${theme.bg} ${theme.text}`}>
      <header className={`border-b ${theme.border}`}>
        <div className="flex h-16 items-center justify-between px-4">
          <HomeLink />
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <ThemeToggle isDark={isDark} onToggle={onToggleTheme} />
          </div>
        </div>
      </header>
      <main className="mx-auto flex min-h-[calc(100dvh-4rem)] max-w-xl flex-col items-center justify-center px-4 text-center">
        <h1 className="text-3xl font-semibold">
          {t("duels.englishOnly.title")}
        </h1>
        <p className={`mt-3 text-sm ${theme.textSecondary}`}>
          {t("duels.englishOnly.description")}
        </p>
      </main>
    </div>
  );
}

function DuelAcceptArticleCards({
  start,
  target,
  theme,
  isDark,
  onArticleSelect,
}: {
  start: Article | undefined;
  target: Article | undefined;
  theme: ReturnType<typeof getThemeColors>;
  isDark: boolean;
  onArticleSelect: (article: {
    title: string;
    description: string | null;
  }) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <SectionHeader
          icon={<SvgIcon markup={boltIcon} size={16} />}
          title={t("duels.startFrom")}
          theme={theme}
          isDark={isDark}
        />
        {start ? (
          <ArticleCard
            article={start}
            theme={theme}
            variant="default"
            onInfoClick={() =>
              onArticleSelect({
                title: start.title,
                description: start.description,
              })
            }
          />
        ) : (
          <DuelArticlePlaceholder
            label={t("duels.waitingStart")}
            theme={theme}
            isDark={isDark}
          />
        )}
      </div>

      <div className="space-y-3">
        <SectionHeader
          icon={<SvgIcon markup={checkCircleIcon} size={16} />}
          title={t("duels.findTarget")}
          theme={theme}
          isDark={isDark}
        />
        {target ? (
          <ArticleCard
            article={target}
            theme={theme}
            variant="default"
            onInfoClick={() =>
              onArticleSelect({
                title: target.title,
                description: target.description,
              })
            }
          />
        ) : (
          <DuelArticlePlaceholder
            label={t("duels.waitingTarget")}
            theme={theme}
            isDark={isDark}
          />
        )}
      </div>
    </div>
  );
}

function DuelArticlePlaceholder({
  label,
  theme,
  isDark,
}: {
  label: string;
  theme: ReturnType<typeof getThemeColors>;
  isDark: boolean;
}) {
  return (
    <div
      className={`p-6 rounded-xl border-2 border-dashed text-center ${isDark ? "border-zinc-800" : "border-gray-200"}`}
    >
      <span className={`text-sm ${theme.textMuted}`}>{label}</span>
    </div>
  );
}

function DuelResultHero({
  phase,
  winner,
  winnerName,
  selfPlayerName,
  opponentPlayerName,
  selfPlayer,
  opponentPlayer,
  isSelfWinner,
  theme,
  isDark,
  onFindNewDuel,
}: {
  phase: "end_screen" | "aborted";
  winner: DuelWinner | undefined;
  winnerName: string;
  selfPlayerName: string;
  opponentPlayerName: string;
  selfPlayer: DuelPlayer | undefined;
  opponentPlayer: DuelPlayer | undefined;
  isSelfWinner: boolean;
  theme: ReturnType<typeof getThemeColors>;
  isDark: boolean;
  onFindNewDuel: () => void;
}) {
  const { t } = useTranslation();
  const isAborted = phase === "aborted";
  const hasWinner = winner === "playerA" || winner === "playerB";
  const isDraw = winner === "draw";
  const isNoWinner = !isAborted && !hasWinner && !isDraw;
  const isLoss = hasWinner && !isSelfWinner;
  const prominentLabel = hasWinner
    ? t("duels.results.winner")
    : t("duels.results.result");
  const prominentValue = hasWinner
    ? isSelfWinner
      ? selfPlayerName
      : winnerName
    : isDraw
      ? t("duels.results.draw")
      : isAborted
        ? t("duels.results.aborted")
        : t("duels.results.noWinner");
  const outcomeValue = isAborted
    ? t("duels.results.cancelled")
    : isDraw
      ? t("duels.results.tie")
      : isNoWinner
        ? t("duels.results.noPath")
        : isSelfWinner
          ? t("duels.results.win")
          : t("duels.results.loss");
  const title = isAborted
    ? t("duels.results.duelAborted")
    : isDraw
      ? t("duels.results.draw")
      : isNoWinner
        ? t("duels.results.noWinner")
        : isSelfWinner
          ? t("duels.results.youWon")
          : t("duels.results.youLost");
  const eyebrow = isAborted
    ? t("duels.results.matchEnded")
    : isDraw
      ? t("duels.results.deadEven")
      : isNoWinner
        ? t("duels.results.unclaimed")
        : isSelfWinner
          ? t("duels.results.victory")
          : t("duels.results.defeat");
  const message = isAborted
    ? t("duels.results.matchEndedNoResult")
    : isDraw
      ? t("duels.results.youAndOpponentFinished", { name: opponentPlayerName })
      : isNoWinner
        ? t("duels.results.neitherCompleted")
        : isSelfWinner
          ? t("duels.results.youHadShortest", { name: selfPlayerName })
          : t("duels.results.opponentBeatYou", { name: winnerName });

  const heroTone = isSelfWinner
    ? isDark
      ? "border-white/20 bg-zinc-900/90 shadow-2xl shadow-white/10"
      : "border-gray-900 bg-white shadow-2xl shadow-gray-900/10"
    : isLoss
      ? isDark
        ? "border-red-500/30 bg-zinc-900/90 shadow-2xl shadow-red-950/30"
        : "border-red-200 bg-white shadow-2xl shadow-red-950/10"
      : isDark
        ? "border-zinc-800 bg-zinc-900/80"
        : "border-gray-200 bg-white shadow-xl shadow-gray-900/5";
  const heroText = theme.text;
  const heroMuted = theme.textSecondary;
  const titleColor = isLoss
    ? isDark
      ? "text-red-400"
      : "text-red-600"
    : heroText;
  const ruleTone = isDark
    ? "border-zinc-800 divide-zinc-800"
    : "border-gray-200 divide-gray-200";
  const iconTone = isSelfWinner
    ? isDark
      ? "bg-white text-black"
      : "bg-gray-900 text-white shadow-lg shadow-gray-900/15"
    : isLoss
      ? isDark
        ? "bg-red-500/10 text-red-400 border border-red-500/20"
        : "bg-red-50 text-red-600 border border-red-200"
      : isDark
        ? "bg-zinc-800 text-zinc-300"
        : "bg-gray-100 text-gray-700";

  return (
    <section
      className={`relative overflow-hidden rounded-2xl border px-5 py-8 text-center md:px-10 md:py-12 ${heroTone}`}
    >
      {isSelfWinner && <WinnerConfetti isDark={isDark} />}
      <div className="relative z-10">
        <div
          className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl ${iconTone}`}
        >
          {isSelfWinner ? (
            <svg
              className="h-8 w-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M8 21h8m-4-4v4m6-17v4a6 6 0 01-12 0V4m12 0h2a1 1 0 011 1v1a4 4 0 01-4 4M6 4H4a1 1 0 00-1 1v1a4 4 0 004 4m-1-6h12"
              />
            </svg>
          ) : isLoss ? (
            <svg
              className="h-8 w-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
          ) : (
            <svg
              className="h-8 w-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          )}
        </div>

        <p
          className={`text-xs font-semibold uppercase tracking-wider ${heroMuted}`}
        >
          {eyebrow}
        </p>
        <h1
          className={`mt-3 text-5xl font-black tracking-normal md:text-7xl ${titleColor}`}
        >
          {title}
        </h1>
        <p
          className={`mx-auto mt-4 max-w-xl text-base md:text-lg ${heroMuted}`}
        >
          {message}
        </p>

        <div className="mt-8">
          <p
            className={`text-xs font-semibold uppercase tracking-wider ${heroMuted}`}
          >
            {prominentLabel}
          </p>
          <p
            className={`mx-auto mt-2 max-w-3xl break-words text-4xl font-black tracking-normal md:text-6xl ${heroText}`}
          >
            {prominentValue}
          </p>
        </div>

        <div
          className={`mt-8 grid grid-cols-1 divide-y border-y text-left sm:grid-cols-3 sm:divide-x sm:divide-y-0 ${ruleTone}`}
        >
          <DuelResultStat
            label={t("duels.yourBest")}
            value={formatDuelBestPathLength(selfPlayer, t)}
            textClassName={heroText}
            mutedClassName={heroMuted}
          />
          <DuelResultStat
            label={t("duels.opponentBest")}
            value={formatDuelBestPathLength(opponentPlayer, t)}
            textClassName={heroText}
            mutedClassName={heroMuted}
          />
          <DuelResultStat
            label={t("duels.outcome")}
            value={outcomeValue}
            textClassName={heroText}
            mutedClassName={heroMuted}
          />
        </div>

        <Button
          variant="primary"
          isDark={isDark}
          onClick={onFindNewDuel}
          className="mx-auto mt-8 max-w-xs"
        >
          {t("duels.findNewDuel")}
        </Button>
      </div>
    </section>
  );
}

function DuelResultStat({
  label,
  value,
  textClassName,
  mutedClassName,
}: {
  label: string;
  value: string;
  textClassName: string;
  mutedClassName: string;
}) {
  return (
    <div className="p-4">
      <div
        className={`text-xs font-semibold uppercase tracking-wider ${mutedClassName}`}
      >
        {label}
      </div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${textClassName}`}>
        {value}
      </div>
    </div>
  );
}

function WinnerConfetti({ isDark }: { isDark: boolean }) {
  const pieces = isDark ? WINNER_CONFETTI_PIECES : LIGHT_WINNER_CONFETTI_PIECES;

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden="true"
    >
      {pieces.map((piece, index) => (
        <span
          key={`${piece.left}-${index}`}
          className={`duel-confetti-piece absolute top-[-12%] rounded-sm ${piece.colorClass} ${piece.shapeClass}`}
          style={
            {
              left: `${piece.left}%`,
              animationDelay: `${piece.delay}ms`,
              animationDuration: `${piece.duration}ms`,
            } satisfies CSSProperties
          }
        />
      ))}
    </div>
  );
}

function TargetFoundFeedback({
  pathLength,
  improvedBest,
  targetTitle,
  theme,
  isDark,
}: {
  pathLength: number;
  improvedBest: boolean;
  targetTitle: string | undefined;
  theme: ReturnType<typeof getThemeColors>;
  isDark: boolean;
}) {
  const { t } = useTranslation();
  const detail = improvedBest
    ? t("duels.savedBest", { count: pathLength })
    : t("duels.thisRunWas", { count: pathLength });

  return (
    <div className="flex h-full w-full items-center justify-center p-6">
      <section
        className={`w-full max-w-xl rounded-2xl border p-8 text-center shadow-2xl ${theme.card} ${theme.cardBorder} ${isDark ? "shadow-white/5" : "shadow-gray-900/10"}`}
      >
        <div
          className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl ${isDark ? "bg-white text-black" : "bg-gray-900 text-white"}`}
        >
          <SvgIcon markup={checkIcon} size={32} />
        </div>
        <p
          className={`text-xs font-semibold uppercase tracking-wider ${theme.textMuted}`}
        >
          {t("duels.targetFound")}
        </p>
        <h2
          className={`mt-3 text-4xl font-black tracking-normal md:text-6xl ${theme.text}`}
        >
          {t("duels.findFaster")}
        </h2>
        {targetTitle && (
          <p className={`mt-4 text-sm ${theme.textSecondary}`}>
            {t("duels.youReached", { target: targetTitle })}
          </p>
        )}
        <p className={`mx-auto mt-3 max-w-md text-sm ${theme.textMuted}`}>
          {detail} {t("duels.restarting")}
        </p>
        <div
          className={`mx-auto mt-6 h-1.5 w-40 overflow-hidden rounded-full ${isDark ? "bg-zinc-800" : "bg-gray-200"}`}
        >
          <div
            className={`duel-target-feedback-progress h-full rounded-full ${isDark ? "bg-white" : "bg-gray-900"}`}
          />
        </div>
      </section>
    </div>
  );
}

function TopBar({
  connected,
  isDark,
  onToggleTheme,
  onOpenHelp,
  center,
  children,
}: {
  connected: boolean;
  isDark: boolean;
  onToggleTheme: () => void;
  onOpenHelp: () => void;
  center?: ReactNode;
  children?: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <header
      className={`relative z-50 shrink-0 border-b ${isDark ? "border-white/[0.08] bg-black/60" : "border-black/[0.06] bg-white/70"} backdrop-blur-xl`}
    >
      <div className="h-14 px-3 sm:px-4 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 sm:gap-4">
        <div className="min-w-0">
          <HomeLink textClassName="hidden md:block" />
        </div>
        <div className="min-w-0 flex items-center justify-center">{center}</div>
        <div className="flex min-w-0 items-center justify-end gap-2 sm:gap-4">
          {children}
          <span
            className={`hidden sm:inline text-xs ${connected ? "text-green-500" : "text-red-500"}`}
          >
            {connected ? t("duels.connected") : t("duels.offline")}
          </span>
          <button
            type="button"
            onClick={onOpenHelp}
            className={`h-10 w-10 rounded-xl border text-sm font-semibold cursor-pointer transition-colors ${isDark ? "bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800" : "bg-white border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`}
            aria-label={t("duels.howDuelsWork")}
            title={t("duels.howDuelsWork")}
          >
            ?
          </button>
          <ThemeToggle isDark={isDark} onToggle={onToggleTheme} />
        </div>
      </div>
    </header>
  );
}

function DuelsHelpDialog({
  isOpen,
  onClose,
  theme,
  isDark,
}: {
  isOpen: boolean;
  onClose: () => void;
  theme: ReturnType<typeof getThemeColors>;
  isDark: boolean;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="duels-help-title"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-md rounded-xl border p-6 shadow-2xl ${theme.card} ${isDark ? "border-zinc-800" : "border-gray-200"}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2
              id="duels-help-title"
              className={`text-lg font-semibold ${theme.text}`}
            >
              {t("duels.dialog.title")}
            </h2>
            <p className={`mt-1 text-sm ${theme.textSecondary}`}>
              {t("duels.dialog.description")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`shrink-0 rounded-lg px-2 py-1 text-sm cursor-pointer ${theme.textMuted} ${isDark ? "hover:bg-zinc-800 hover:text-white" : "hover:bg-gray-100 hover:text-gray-900"}`}
            aria-label={t("common.close")}
          >
            {t("common.close")}
          </button>
        </div>

        <ul className={`space-y-3 text-sm leading-6 ${theme.textSecondary}`}>
          <li>{t("duels.dialog.step1")}</li>
          <li>{t("duels.dialog.step2")}</li>
          <li>{t("duels.dialog.step3")}</li>
          <li>{t("duels.dialog.step4")}</li>
          <li>{t("duels.dialog.step5")}</li>
          <li>{t("duels.dialog.step6")}</li>
        </ul>
      </div>
    </div>
  );
}

function DuelTargetSummary({
  target,
  theme,
  isDark,
}: {
  target: Article | undefined;
  theme: ReturnType<typeof getThemeColors>;
  isDark: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="min-w-0 flex items-center justify-center gap-2">
      <span
        className={`hidden sm:inline text-xs font-medium uppercase tracking-wider ${theme.textMuted}`}
      >
        {t("duels.target")}
      </span>
      {target && (
        <TargetBadge
          article={target}
          isFound={false}
          theme={theme}
          isDark={isDark}
          className="max-w-[44vw] sm:max-w-[28vw] rounded-full"
        />
      )}
    </div>
  );
}

function OpponentStatsPanel({
  opponent,
  opponentName,
  theme,
  isDark,
}: {
  opponent: DuelPlayer | undefined;
  opponentName: string;
  theme: ReturnType<typeof getThemeColors>;
  isDark: boolean;
}) {
  const { t } = useTranslation();
  const opponentBestLength = getBestTryLength(opponent);
  const opponentHasBestPath = opponentBestLength !== "-";
  const opponentFinishAction = opponentHasBestPath
    ? t("duels.imDone")
    : t("duels.giveUp");

  return (
    <div
      className={`${theme.card} ${theme.cardBorder} shrink-0 rounded-xl p-4`}
    >
      <div className="mb-3 flex items-center justify-between">
        <SectionHeader
          icon={<OpponentIcon />}
          title={opponentName}
          theme={theme}
          isDark={isDark}
        />
        {opponent?.wantsFinish && (
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-medium ${
              opponentHasBestPath
                ? "bg-green-500/10 text-green-500"
                : "bg-amber-500/10 text-amber-500"
            }`}
          >
            {opponentFinishAction}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <OpponentStat
          label={t("duels.current")}
          value={getCurrentTryLength(opponent)}
          theme={theme}
        />
        <OpponentStat
          label={t("duels.best")}
          value={opponentBestLength}
          theme={theme}
        />
      </div>
      {opponent?.wantsFinish && (
        <p className={`mt-3 text-sm ${theme.textSecondary}`}>
          {t("duels.opponentPressed", {
            name: opponentName,
            action: opponentFinishAction,
          })}
        </p>
      )}
    </div>
  );
}

function OpponentStat({
  label,
  value,
  theme,
}: {
  label: string;
  value: number | string;
  theme: ReturnType<typeof getThemeColors>;
}) {
  return (
    <div className={`rounded-lg border ${theme.borderSubtle} p-3`}>
      <div
        className={`text-xs font-medium uppercase tracking-wider ${theme.textMuted}`}
      >
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function getPlayerInfo(
  player: DuelPlayer | undefined,
  playerInfos: Record<string, DuelPlayerInfo>,
): DuelPlayerInfo | undefined {
  if (!player) return undefined;
  return playerInfos[String(player.id)];
}

function getPlayerDisplayName(
  player: DuelPlayer | undefined,
  playerInfos: Record<string, DuelPlayerInfo>,
  fallback: string,
): string {
  const playerInfo = getPlayerInfo(player, playerInfos);
  if (playerInfo?.name) return `Player ${playerInfo.name}`;
  if (player) return `Player ${player.id}`;
  return fallback;
}

function getWinnerDisplayName(
  winner: string | undefined,
  playerA: DuelPlayer | undefined,
  playerB: DuelPlayer | undefined,
  playerInfos: Record<string, DuelPlayerInfo>,
): string {
  if (winner === "playerA")
    return getPlayerDisplayName(playerA, playerInfos, "Player A");
  if (winner === "playerB")
    return getPlayerDisplayName(playerB, playerInfos, "Player B");
  if (winner === "draw") return "Draw";
  return "None";
}

function getDuelWinnerPlayer(
  winner: DuelWinner | undefined,
  playerA: DuelPlayer | undefined,
  playerB: DuelPlayer | undefined,
): DuelPlayer | undefined {
  if (winner === "playerA") return playerA;
  if (winner === "playerB") return playerB;
  return undefined;
}

function getCurrentTryLength(player: DuelPlayer | undefined): number {
  return player?.currentTry?.length ?? player?.currentTryLength ?? 0;
}

function getBestTryLength(player: DuelPlayer | undefined): number | string {
  return player?.bestTry?.length ?? player?.bestTryLength ?? "-";
}

function getBestPathArticleCount(
  player: DuelPlayer | undefined,
): number | null {
  if (player?.bestTry) return player.bestTry.length;
  if (typeof player?.bestTryLength === "number") return player.bestTryLength;
  return null;
}

function formatDuelBestPathLength(
  player: DuelPlayer | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const count = getBestPathArticleCount(player);
  if (count === null) return "-";
  return t("duels.articles", { count });
}

function notifyDuelFound(title: string, body: string) {
  playDuelFoundSound();

  if (
    typeof window !== "undefined" &&
    "Notification" in window &&
    document.hidden &&
    Notification.permission === "granted"
  ) {
    new Notification(title, {
      body,
    });
  }
}

function playDuelFoundSound() {
  if (typeof window === "undefined") return;

  const AudioContextCtor =
    window.AudioContext ||
    (window as Window & { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!AudioContextCtor) return;

  try {
    const audioContext = new AudioContextCtor();
    const startAt = audioContext.currentTime;

    playTone(audioContext, 660, startAt, 0.12);
    playTone(audioContext, 880, startAt + 0.14, 0.16);

    window.setTimeout(() => {
      audioContext.close().catch(() => undefined);
    }, 500);
  } catch {
    // Browsers can block audio until the user has interacted with the page.
  }
}

function playTone(
  audioContext: AudioContext,
  frequency: number,
  startAt: number,
  duration: number,
) {
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();

  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(frequency, startAt);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(0.18, startAt + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

  oscillator.connect(gain);
  gain.connect(audioContext.destination);
  oscillator.start(startAt);
  oscillator.stop(startAt + duration + 0.02);
}

function AcceptStatus({
  label,
  accepted,
  theme,
}: {
  label: string;
  accepted: boolean;
  theme: ReturnType<typeof getThemeColors>;
}) {
  const { t } = useTranslation();

  return (
    <div
      className={`flex items-center justify-between rounded-xl border ${theme.border} p-4`}
    >
      <span className="text-sm font-medium">{label}</span>
      <span
        className={`text-sm ${accepted ? "text-green-500" : theme.textMuted}`}
      >
        {accepted ? t("duels.accepted") : t("duels.waiting")}
      </span>
    </div>
  );
}

function PathPanel({
  title,
  path,
  theme,
  isDark,
}: {
  title: string;
  path: Article[];
  theme: ReturnType<typeof getThemeColors>;
  isDark: boolean;
}) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLOListElement | null>(null);
  const { fullscreenRef, isFullscreen, toggleFullscreen } =
    useFullscreen<HTMLDivElement>();

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    list.scrollTop = list.scrollHeight;
  }, [path.length]);

  return (
    <div
      ref={fullscreenRef}
      className={`${theme.card} ${theme.cardBorder} flex min-h-0 flex-1 flex-col rounded-xl p-4 ${isFullscreen ? "h-dvh flex-none" : ""}`}
    >
      <div className="mb-3 flex shrink-0 items-center justify-between">
        <SectionHeader
          icon={<PathIcon />}
          title={title}
          theme={theme}
          isDark={isDark}
        />
        <div className="flex items-center gap-2">
          <span className={`text-xs ${theme.textMuted}`}>
            {t("duels.articles", { count: path.length })}
          </span>
          <FullscreenButton
            isDark={isDark}
            isFullscreen={isFullscreen}
            onClick={() => void toggleFullscreen()}
            showLabel={false}
            testId="path-panel-fullscreen-toggle"
          />
        </div>
      </div>
      {path.length === 0 ? (
        <p className={`text-sm ${theme.textMuted}`}>{t("duels.noPathYet")}</p>
      ) : (
        <ol
          ref={listRef}
          className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1"
        >
          {path.map((article, index) => (
            <li key={`${article.id}-${index}`} className="text-sm">
              <span className={theme.textMuted}>{index + 1}.</span>{" "}
              {article.title}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function PathIcon() {
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
      />
    </svg>
  );
}

function OpponentIcon() {
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 19a6 6 0 00-12 0m12 0h6m-6 0a6 6 0 0112 0M9 11a4 4 0 100-8 4 4 0 000 8zm12-2a3 3 0 10-6 0 3 3 0 006 0z"
      />
    </svg>
  );
}
